import { useQuery, useQueryClient } from "@tanstack/react-query";
import { checkReading, type MeterReadingBody, type MeterRoundDetailDto } from "@klantpunt/shared";
import { api } from "../../lib/api";
import { queuePhoto } from "../../lib/photos";
import { queue } from "../../offline/queue";
import { useQueueState } from "../../offline/state";

/**
 * Veldflow meterstanden (docs/METERSTANDEN.md §8): opnames gaan via de offline-wachtrij
 * (idempotent op het id) en staan meteen in de bewaarde cache, met de controle uit §5 lokaal
 * berekend. De server controleert opnieuw.
 */
export const roundKey = (id: string) => ["meter-round", id] as const;

export function useRoundDetail(id: string) {
  return useQuery({ queryKey: roundKey(id), queryFn: () => api<MeterRoundDetailDto>(`/meters/rounds/${id}`) });
}

/** Opnames van deze ronde die nog op verbinding wachten. */
export function usePendingRoundOps(roundId: string) {
  useQueueState();
  return queue.mine().filter((op) => op.req.kind === "http" && op.req.path.startsWith(`/meters/rounds/${roundId}/`))
    .length;
}

export function useMeterRound(roundId: string, buildingId: string) {
  const qc = useQueryClient();
  const key = roundKey(roundId);
  return {
    /** Foto eerst in de wachtrij (upload), daarna de opname; geeft het file-id. */
    photo: (file: File) => queuePhoto(file, buildingId),
    async save(body: MeterReadingBody & { values: NonNullable<MeterReadingBody["values"]> }, label: string) {
      const d = qc.getQueryData<MeterRoundDetailDto>(key);
      if (d) {
        qc.setQueryData<MeterRoundDetailDto>(key, {
          ...d,
          meters: d.meters.map((m) => {
            if (m.asset.id !== body.assetId) return m;
            const point = {
              id: body.id,
              readOn: body.readOn,
              unreachable: Boolean(body.unreachable),
              event: body.event ?? "normal",
              values: body.unreachable ? [] : body.values,
            };
            return {
              ...m,
              reading: {
                ...point,
                assetId: body.assetId,
                roundId,
                unreachableReason: body.unreachableReason ?? null,
                photoFileId: body.photoFileId ?? null,
                remark: body.remark ?? null,
                findingId: m.reading?.findingId ?? null,
                findingCode: m.reading?.findingCode ?? null,
                source: "round",
                createdBy: null,
                createdAt: new Date().toISOString(),
              },
              check: checkReading({ ...m.meter, assetId: m.asset.id }, m.history, point, d.vacancies),
            };
          }),
        });
      }
      // Eigen op-id per keer opslaan: een correctie van dezelfde opname is een nieuw verzoek.
      const r = await queue.enqueue<MeterRoundDetailDto>({
        buildingId,
        label,
        req: { kind: "http", method: "POST", path: `/meters/rounds/${roundId}/readings`, body },
      });
      if (r.status === "done") qc.setQueryData(key, r.data);
      return r.status;
    },
  };
}
